import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import SideBar from "../../components/Sidebar";
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import "../../../node_modules/bootstrap/dist/js/bootstrap.min.js";
import { ASK_JOB_COACH } from "../../utils/constants";
import BouncingDotsLoader from "../../components/LoadingDots";
import Chat, { ChatMessage } from "../../components/Chat";
import ChatComposer from "../../components/ChatComposer";
import useWindowWidth from "../../utils/windowWidth";
import GuestLoginPrompt from "../../components/GuestLoginPrompt";
import { clearGuestResumeSession, getGuestAwareHeaders, storeGuestIdFromHeaders } from "../../utils/guestSession";

// import io from "socket.io-client";

function OpportunityBot() {
  const mainRef = useRef<HTMLHeadingElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const useResumeContext = (
    location.state as { useResumeContext?: boolean } | null
  )?.useResumeContext === true;
  const isAuthenticated = Boolean(localStorage.getItem("token"));

  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState("");
  const [sendDisabled, setSendDisabled] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [resumeUploadRequired, setResumeUploadRequired] = useState(false);
  const [uuid] = useState(() => crypto.randomUUID());

  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { console.log(chat) }, [chat]);

  function handlePromptSubmit(): void {
    const messageText = prompt.trim();
    if (!messageText || sendDisabled || resumeUploadRequired) return;
    setSendDisabled(true);
    setChat((prevChat) => {
      return [...prevChat, { sender: "user", text: messageText, timestamp: new Date().toISOString() }];
    });
    sendChatMessage(messageText);
    setPrompt("");
  }

  /*
  const fetchChats = () => {
    const url = FETCH_JOB_CHATS;
    makeAuthenticatedGet(url)
      .then((response) => {
        // console.log("chats", response);
        setJobChatId(response[0].sessionId);
        response[0].messages.map((msg: { text: string }) => {
          setChat((chat) => [...chat, msg.text]);
          return "";
        });
      })
      .catch((error) => {
        console.log("cannot fetch chats", error);
      });
  };
  */



  const sendChatMessage = async (messageText: string) => {
    try {
      // Don't add empty message here - wait for first chunk
      const response = await fetch(ASK_JOB_COACH, {
        method: 'POST',
        headers: getGuestAwareHeaders(),
        body: JSON.stringify({
          message: messageText,
          uuid,
          stream: true,
          useResumeContext,
        })
      });

      storeGuestIdFromHeaders(response.headers);

      if (!response.ok) {
        if (
          response.status === 404 &&
          useResumeContext &&
          !localStorage.getItem("token")
        ) {
          clearGuestResumeSession();
          setResumeUploadRequired(true);
          return;
        }
        throw new Error(`Request failed with status ${response.status}`);
      }
      if (!response.body) {
        throw new Error("The response did not include a message stream");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let isFirstChunk = true;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);

        // On first chunk: create bot message and hide loading
        if (isFirstChunk) {
          setIsStreaming(true);
          setChat((prev) => [...prev, {
            text: chunk,
            timestamp: new Date().toISOString(),
            sender: "bot",
          }]);
          isFirstChunk = false;
        } else {
          // Subsequent chunks: append to existing message
          setChat((prev) => {
            const updated = [...prev];
            updated[updated.length - 1].text += chunk;
            return updated;
          });
        }
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      setChat((prev) => [...prev, {
        text: "I’m sorry, I couldn’t complete that response. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        sender: "bot",
      }]);
    } finally {
      setSendDisabled(false);
      setIsStreaming(false);
    }
  };

  // NEW: Combined useEffect to fix race condition
  useEffect(() => {
    document.title = "Career Discovery chatbot - Nclude Portal";

    setChat([{
      text: "Hey there! I'm your Job Coach — I can help you explore career paths, find opportunities, and prepare for your next step. What's on your mind?",
      timestamp: new Date().toISOString(),
      sender: "bot",
    }]);
  }, []);

  const liveRegionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set focus on the main content when the route changes
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  //window size
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;


  // if (!socket) {
  //   return <div>Loading...</div>;
  // }

  return (
    <div className="containerr">
      <GuestLoginPrompt serviceName="Career Discovery" promptKey="career-discovery" />
      {!isMobile && <Header />}
      <div className="page-container">
        <SideBar activePage="Opportunity" />
        <main className="doc-container agent-chat-page" id="main-content" tabIndex={-1}>
          <div className="agent-chat-heading">
            <div>
              {/* <p className="agent-eyebrow">AI coach</p> */}
              <h1 className="display-title" ref={mainRef} tabIndex={-1}>Career Discovery</h1>
              <p className="agent-description">Explore career paths, prepare for applications, and plan your next step.</p>
              <p className={`agent-context-note ${useResumeContext ? "is-enabled" : ""}`}>
                {useResumeContext ? (
                  <>
                    <strong>Using your {isAuthenticated ? "saved" : "temporary"} resume.</strong>
                    {isAuthenticated ? (
                      <span>To replace it, go to <Link to="/edit-profile">Profile settings</Link>.</span>
                    ) : (
                      <span><Link to="/opportunity/new" state={{ replaceResume: true }}>Upload a different resume</Link> if needed.</span>
                    )}
                  </>
                ) : (
                  <>Resume context is off. The coach will use only what you share in this conversation.</>
                )}
              </p>
            </div>
            <p className="agent-status" role="status" aria-live="polite">
              <span className={sendDisabled || isStreaming ? "status-dot busy" : "status-dot"} aria-hidden="true" />
              {sendDisabled || isStreaming ? "Responding" : "Online"}
            </p>
          </div>
          {resumeUploadRequired && (
            <div className="guest-resume-expired" role="alert">
              <div>
                <h2>Upload your resume again</h2>
                <p>Your temporary resume information is unavailable or has expired. Upload your resume again to continue with personalized career guidance.</p>
              </div>
              <button type="button" onClick={() => navigate("/opportunity/new")}>Upload resume</button>
            </div>
          )}
          <section className="chat-box">
            <div
              className="chat-window-container"
              aria-busy={sendDisabled}
            >
              {chat.length === 0 ? <BouncingDotsLoader /> : (
                <Chat
                  chat={chat}
                  isStreaming={isStreaming}
                  assistantName="Career Coach"
                  announcementRef={liveRegionRef}
                />
              )}
              {sendDisabled && !isStreaming && (
                <div className="assistant-thinking" role="status">
                  <BouncingDotsLoader />
                  <span>Career Coach is thinking…</span>
                </div>
              )}
            </div>
            <div ref={liveRegionRef} aria-live="polite" aria-atomic="true" className="visually-hidden" />
            <ChatComposer
              inputRef={inputRef}
              value={prompt}
              onChange={setPrompt}
              onSubmit={handlePromptSubmit}
              busy={sendDisabled}
              disabled={resumeUploadRequired}
              placeholder={resumeUploadRequired ? "Upload your resume again to continue" : "Message your career coach"}
              assistantName="Career Coach"
            />
          </section>
        </main>
      </div>
    </div>
  );
}

export default OpportunityBot;
